import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { ROUTE_BUILDER } from '@/constants/pages';
import { getGeoLocation, isServer } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import { safeEncodeURIComponent } from '@/helpers/urlTs';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
} from '@/hooks/search/useSearch.constants';
import { buildUrlSearchParams, getSearchUrlParameters, positionSchema } from '@/hooks/search/useSearch.helpers';
import { _s } from '@/locale';
import { Tracking } from '@/types/analytics';
import { MouseEvent } from 'react';
import { toast } from 'react-toastify';
import {
  getLastLocationSuggestionFromLocalStorage,
  SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION,
} from '../searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { SearchContext } from '../searchV2/types/tracking';
import { TabIdentifier } from './MegaMenu.types';
import { baseTranslationKey } from './modules/Category';
import { MegaMenuSubCategory } from './modules/Category.types';

export function handleOnCategoryLinkClick(
  e: MouseEvent<HTMLAnchorElement | HTMLButtonElement | HTMLDivElement>,
  to: string,
  history: any /** no types for history api with out react-rouder-dom version */,
  context: SearchContext,
  cb?: () => void,
) {
  /**
   * If user clicks a link with the "near me" location we need to fetch the user's geolocation
   * and then navigate to the search page with the user's location.
   */
  if (to.includes(encodeURIComponent(DEFAULT_MY_LOCATION_KEYWORD))) {
    e.preventDefault();
    /**
     * isFetchingUserPosition check in location state is done inside location search form input to display the loading spinner
     * if user's geolocation is being fetched.
     */
    const url: {
      pathname: string;
      search?: string;
      state: { isFetchingUserPosition: boolean; searchContext: SearchContext };
    } = {
      pathname: to,
      state: { isFetchingUserPosition: true, searchContext: context },
    };

    const goto = (location: { pathname: string; search?: string; state?: { isFetchingUserPosition: boolean } }) => {
      /**
       * Timeout to ensure loading state in location input shows
       */
      location['state']['searchContext'] = context;
      setTimeout(() => history.replace(location), 1);
    };

    getGeoLocation(
      (lat: string, lon: string) => {
        goto({ ...url, search: `?lat=${lat}&lon=${lon}`, state: { isFetchingUserPosition: false } });
      },
      () => {},
      () => {
        /**
         * on loading geo location
         */
        goto({ pathname: to, state: { isFetchingUserPosition: true } });
      },
      () => {
        toast(
          ({ closeToast }) => (
            <Snackbar onClose={closeToast} label={_s(`${baseTranslationKey}.snackbar.error.userLocationDenied`)} />
          ),
          { autoClose: false },
        );
        goto({ pathname: to, state: { isFetchingUserPosition: false } });
      },
    );
  }

  cb?.();
}

/**
 * Builds the link and keeps current search location if it is not the default one
 * and keeps the search parameters from the current search for a better user experience.
 *
 */
export function buildClientCategoryLink(
  to: string,
  match: any /** no types for routeMatch api with out react-rouder-dom version */,
  context: SearchContext,
  keepPosition?: boolean,
) {
  const currentLocation = match?.params?.location;
  const currentLocationNotDefault = currentLocation && currentLocation !== DEFAULT_SEARCH_LOCATION;
  const urlState = getSearchUrlParameters({ match });

  let [pathname, searchQuery] = to.split('?');
  let search = to.split('?')?.[1] || undefined;

  if (currentLocationNotDefault) {
    if (to.includes(DEFAULT_SEARCH_LOCATION)) {
      pathname = pathname.replace(DEFAULT_SEARCH_LOCATION, safeEncodeURIComponent(currentLocation));
    }
  }

  if (searchQuery) {
    search = searchQuery;
    const position = `${urlState['lat']}-${urlState['lon']}`;
    /**
     * if the to link includes a search query (like ?gc=1 etc)
     * we reset the search query to that but we still need
     * to keep the lat & lon if the user is searching for "Nära mig" (currentLocation)
     */
    if (currentLocation === DEFAULT_MY_LOCATION_KEYWORD && positionSchema.safeParse(position).success) {
      search = search + (search ? '&' : '') + `lat=${urlState['lat']}&lon=${urlState['lon']}`;
    }
  } else {
    let _urlState = { ...urlState };
    if (!keepPosition) {
      delete _urlState.lat;
      delete _urlState.lon;
    }
    /**
     * dont keep prefs in mega menu links/tabs
     */
    delete _urlState.prefs;
    search = buildUrlSearchParams(_urlState, _urlState).toString();
  }

  return { pathname, search, state: { searchContext: context } };
}

export function trackMegaMenuClick(
  tab: TabIdentifier,
  cta: MegaMenuSubCategory['links'][number] | null,
  screenName: Tracking.ScreenName,
) {
  trackMpEvent({
    name: 'mega_menu_click',
    properties: {
      screen_name: screenName,
      mega_menu_tab: tab,
      mega_menu_cta: cta?.label?.replace?.(/ /g, '_'),
    },
  });
}

export function buildSistaminutenLink(keyword = DEFAULT_SEARCH_KEYWORD, location = DEFAULT_SEARCH_LOCATION) {
  if (!isServer) {
    const noDefaultLocation = sessionStorage.getItem(SESSION_STORAGE_KEY_NO_DEFAULT_LOCATION);

    if (!noDefaultLocation) {
      location = getLastLocationSuggestionFromLocalStorage()?.label || DEFAULT_SEARCH_LOCATION;
    }
  }

  return ROUTE_BUILDER.sistaMinutenSerp(keyword, location);
}
